import React from 'react';
import dayjs from 'dayjs';
import { Col, Row } from '../../../lib/utils';
import ProfileImage from '../../ProfileImage';
import { resolveMembershipName } from '../../../lib/membershipDisplay';

const STATUS_STYLE: Record<string, { label: string; bg: string; color: string; border: string }> = {
    ATTENDANCE: { label: '✓ 출석', bg: '#DCFCE7', color: '#15803D', border: '#BBF7D0' },
    NOSHOW: { label: '✕ 노쇼', bg: '#FEE2E2', color: '#B91C1C', border: '#FECACA' },
};

const DAY_ACCENT = ['#EFF6FF', '#F0FDF4', '#FFF7ED', '#F5F3FF', '#FEF2F2'];

const formatTimeRange = (start: string, end: string) =>
    `${dayjs(start).format('A h:mm')} ~ ${dayjs(end).format('A h:mm')}`;

export interface ClassEntry {
    source?: 'pt' | 'group';
    groupScheduleId?: string;
    startTime: string;
    endTime: string;
    alias: string;
    profileImageUrl?: string;
    profileName?: string;
    profileColorType?: any;
    membershipName?: string;
    ticketName?: string;
    name?: string;
    membership?: {
        name?: string;
        membershipName?: string;
        ticketName?: string;
    };
    membershipInfo?: {
        membership?: {
            name?: string;
        };
    };
    currentSession?: number | null;
    totalSession?: number | null;
    attendanceStatus?: string;
}

interface Props {
    date: string;
    classes: ClassEntry[];
    accentIdx?: number;
}

const ClassDayCard: React.FC<Props> = ({ date, classes, accentIdx = 0 }) => {
    if (!classes || classes.length === 0) return null;

    const d = dayjs(date);
    const accent = DAY_ACCENT[accentIdx % DAY_ACCENT.length];

    return (
        <Col
            style={{
                borderRadius: 12,
                backgroundColor: '#FFFFFF',
                border: '1px solid #E5E7EB',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(15,23,42,0.04)',
            }}
        >
            {/* 날짜 헤더 — 컴팩트 */}
            <Row
                style={{
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 14px',
                    backgroundColor: accent,
                    borderBottom: '1px solid #E5E7EB',
                }}
            >
                <span style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>
                    {d.format('M월 D일')}
                </span>
                <span style={{ fontSize: 13, color: '#64748B', fontWeight: 600 }}>
                    ({d.format('ddd')})
                </span>
                <span style={{ fontSize: 12, color: '#94A3B8' }}>·</span>
                <span style={{ fontSize: 13, color: '#475569' }}>
                    총 <b style={{ color: '#2563EB', fontSize: 14 }}>{classes.length}</b>회
                </span>
            </Row>

            {/* 수업 목록 */}
            <Col>
                {classes.map((cls, idx) => {
                    const isGroupClass = cls.source === 'group' || !!cls.groupScheduleId;
                    const isAttendance = !cls.attendanceStatus || cls.attendanceStatus === 'ATTENDANCE';
                    const statusCfg = isAttendance ? STATUS_STYLE.ATTENDANCE : STATUS_STYLE.NOSHOW;
                    const membershipName = resolveMembershipName(cls);
                    return (
                        <Row
                            key={idx}
                            style={{
                                alignItems: 'center',
                                padding: '8px 14px',
                                borderTop: idx === 0 ? 'none' : '1px solid #F3F4F6',
                                gap: 16,
                            }}
                        >
                            {/* 회원 */}
                            <Row style={{ alignItems: 'center', gap: 10, flex: '0 0 200px' }}>
                                <ProfileImage
                                    imageUrl={cls.profileImageUrl}
                                    type="VerySmall"
                                    profileName={cls.profileName}
                                    profileColorType={cls.profileColorType}
                                />
                                <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                                    {cls.alias}
                                </span>
                            </Row>

                            {/* 시간 */}
                            <Row style={{ alignItems: 'center', gap: 6, flex: '0 0 220px' }}>
                                <span style={{ fontSize: 13 }}>🕐</span>
                                <span style={{ fontSize: 14, color: '#1F2937', fontWeight: 600 }}>
                                    {formatTimeRange(cls.startTime, cls.endTime)}
                                </span>
                            </Row>

                            {/* 수강권 + 회차 */}
                            <Row style={{ alignItems: 'center', gap: 8, flex: '1 1 auto', minWidth: 0 }}>
                                <span
                                    style={{
                                        padding: '3px 10px',
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        backgroundColor: '#F1F5F9',
                                        color: '#334155',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {membershipName || '-'}
                                </span>
                                {isGroupClass ? (
                                    <span
                                        style={{
                                            padding: '3px 10px',
                                            borderRadius: 999,
                                            fontSize: 12,
                                            fontWeight: 700,
                                            backgroundColor: '#DBEAFE',
                                            color: '#1D4ED8',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        그룹수업
                                    </span>
                                ) : (
                                    <span style={{ fontSize: 13, color: '#6B7280', fontWeight: 600 }}>
                                        {cls.currentSession ?? '-'}
                                        <span style={{ color: '#9CA3AF', fontWeight: 500 }}>
                                            /{cls.totalSession ?? '-'}회
                                        </span>
                                    </span>
                                )}
                            </Row>

                            {/* 상태 */}
                            <span
                                style={{
                                    padding: '3px 10px',
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    backgroundColor: statusCfg.bg,
                                    color: statusCfg.color,
                                    border: `1px solid ${statusCfg.border}`,
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                }}
                            >
                                {statusCfg.label}
                            </span>
                        </Row>
                    );
                })}
            </Col>
        </Col>
    );
};

export default ClassDayCard;
